import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config';
import { api } from '../../utils/apiUtils';

// Async thunk to fetch boards by workspace
export const fetchBoards = createAsyncThunk(
    'boards/fetchBoards',
    async ({ workspaceId, useCache = false } = {}, { rejectWithValue }) => {
        try {
            // Build URL with workspaceId if provided
            const url = workspaceId
                ? `${config.API_URI}/v1/boards?workspaceId=${workspaceId}`
                : `${config.API_URI}/v1/boards`;

            // Use the enhanced API utility with caching
            return await api.get(
                url,
                {},
                useCache,
                5 * 60 * 1000 // Cache for 5 minutes
            );
        } catch (error) {
            console.error('Error fetching boards:', error);
            return rejectWithValue(error.message);
        }
    }
);

export const fetchBoardLists = createAsyncThunk(
    'boards/fetchBoardLists',
    async (params, { rejectWithValue }) => {
        try {
            // Handle both string boardId and object with options
            const boardId = typeof params === 'string' ? params : params.boardId;
            const includeCards = typeof params === 'object' && params.includeCards !== undefined
                ? params.includeCards
                : false; // Default to false for better performance
            const useCache = typeof params === 'object' && params.useCache !== undefined
                ? params.useCache
                : true; // Default to using cache

            const url = `${config.API_URI}/v1/boards/${boardId}/lists?includeCards=${includeCards}`;

            // Use the enhanced API utility with caching
            const actionLists = await api.get(
                url,
                {},
                useCache,
                3 * 60 * 1000 // Cache for 3 minutes
            );

            return { boardId, actionLists };
        } catch (error) {
            console.error('Error fetching board lists:', error);
            return rejectWithValue(error.message);
        }
    }
);

// Async thunk to fetch starred boards
export const fetchStarredBoards = createAsyncThunk(
    'boards/fetchStarredBoards',
    async ({ useCache = false } = {}, { rejectWithValue }) => {
        try {

            // Use the enhanced API utility with caching
            return await api.get(
                `${config.API_URI}/v1/users/me/get-starred`,
                {},
                useCache,
                5 * 60 * 1000 // Cache for 5 minutes
            );
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred while fetching starred boards');
        }
    }
);

export const fetchRecentBoards = createAsyncThunk(
    'boards/fetchRecentBoards',
    async ({ limit = 5, useCache = false } = {}, { rejectWithValue }) => {
        try {
            // Use the enhanced API utility with caching
            return await api.get(
                `${config.API_URI}/v1/users/me/recent-viewed?limit=${limit}`,
                {},
                useCache,
                2 * 60 * 1000 // Cache for 2 minutes
            );
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred while fetching recent boards');
        }
    }
);

// Async thunk to fetch archived boards by workspace
export const fetchArchivedBoards = createAsyncThunk(
    'boards/fetchArchivedBoards',
    async ({ workspaceId } = {}, { rejectWithValue }) => {
        try {
            const url = workspaceId
                ? `${config.API_URI}/v1/boards?workspaceId=${workspaceId}&archived=true`
                : `${config.API_URI}/v1/boards?archived=true`;

            return await api.get(url);
        } catch (error) {
            console.error('Error fetching archived boards:', error);
            return rejectWithValue(error.message);
        }
    }
);

export const fetchBoardsByWorkspaceAndMember = createAsyncThunk(
    'boards/fetchBoardsByWorkspaceAndMember',
    async ({ workspaceId, memberId, useCache = false }, { rejectWithValue }) => {
        try {
            // Use the enhanced API utility with caching
            return await api.get(
                `${config.API_URI}/v1/members/${workspaceId}/${memberId}/boards`,
                {},
                useCache,
                5 * 60 * 1000 // Cache for 5 minutes
            );
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch boards for the member in the workspace');
        }
    }
);

const boardsSlice = createSlice({
    name: 'boards',
    initialState: {
        boards: [],
        archivedBoards: [],
        memberBoards: [],
        starredBoards: [],
        recentBoards: [],
        actionLists: {},
        status: 'idle',
        archivedStatus: 'idle',
        starredStatus: 'idle',
        recentStatus: 'idle',
        error: null,
    },
    reducers: {
        // Add a reducer to clear cache when needed
        clearBoardsCache: (state) => {
            // This is just to trigger a re-fetch, the actual cache clearing happens in the component
            state.status = 'idle';
        },

        // ========== OPTIMISTIC ACTIONS FOR REAL-TIME UPDATES ==========

        // Update board properties in all board arrays (boards, starredBoards, recentBoards, memberBoards)
        optimisticUpdateBoardInLists: (state, action) => {
            const { boardId, updates } = action.payload;

            // Helper function to update board in an array
            const updateBoardInArray = (boardsArray) => {
                const index = boardsArray.findIndex(
                    board => board._id === boardId || board.shortId === boardId
                );
                if (index !== -1) {
                    boardsArray[index] = {
                        ...boardsArray[index],
                        ...updates
                    };
                }
            };

            // Update in all board arrays
            updateBoardInArray(state.boards);
            updateBoardInArray(state.starredBoards);
            updateBoardInArray(state.recentBoards);
            updateBoardInArray(state.memberBoards);
        },

        // Add new board to boards array (for BOARD_CREATED events)
        optimisticAddBoardToLists: (state, action) => {
            const board = action.payload;

            // Check if board already exists in boards array (avoid duplicates)
            const existsInBoards = state.boards.some(
                b => b._id === board._id || b.shortId === board.shortId
            );

            if (!existsInBoards) {
                // Add to beginning of boards array (most recent)
                state.boards.unshift(board);
            }
        },

        // Remove board from all arrays (for BOARD_DELETED events)
        optimisticRemoveBoardFromLists: (state, action) => {
            const boardId = action.payload;

            // Helper function to remove board from an array
            const removeBoardFromArray = (boardsArray) => {
                return boardsArray.filter(
                    board => board._id !== boardId && board.shortId !== boardId
                );
            };

            // Remove from all board arrays
            state.boards = removeBoardFromArray(state.boards);
            state.archivedBoards = removeBoardFromArray(state.archivedBoards);
            state.starredBoards = removeBoardFromArray(state.starredBoards);
            state.recentBoards = removeBoardFromArray(state.recentBoards);
            state.memberBoards = removeBoardFromArray(state.memberBoards);
        },

        // Remove board from archived list (for unarchive actions)
        optimisticRemoveFromArchivedBoards: (state, action) => {
            const boardId = action.payload;
            state.archivedBoards = state.archivedBoards.filter(
                board => board._id !== boardId && board.shortId !== boardId
            );
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch all boards
            .addCase(fetchBoards.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchBoards.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // The API now returns an array of boards directly
                state.boards = action.payload;
            })
            .addCase(fetchBoards.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Fetch archived boards
            .addCase(fetchArchivedBoards.pending, (state) => {
                state.archivedStatus = 'loading';
            })
            .addCase(fetchArchivedBoards.fulfilled, (state, action) => {
                state.archivedStatus = 'succeeded';
                state.archivedBoards = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchArchivedBoards.rejected, (state, action) => {
                state.archivedStatus = 'failed';
                state.error = action.payload;
            })
            // Fetch boards by workspace and member
            .addCase(fetchBoardsByWorkspaceAndMember.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchBoardsByWorkspaceAndMember.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // Handle both old and new API response formats
                state.memberBoards = action.payload.boards || action.payload;
            })
            .addCase(fetchBoardsByWorkspaceAndMember.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Fetch board lists
            .addCase(fetchBoardLists.pending, () => {
                // No need to set global loading state for lists
            })
            .addCase(fetchBoardLists.fulfilled, (state, action) => {
                state.actionLists[action.payload.boardId] = action.payload.actionLists;
            })
            .addCase(fetchBoardLists.rejected, (state, action) => {
                state.error = action.payload;
            })
            // Fetch starred boards
            .addCase(fetchStarredBoards.pending, (state) => {
                state.starredStatus = 'loading';
            })
            .addCase(fetchStarredBoards.fulfilled, (state, action) => {
                state.starredStatus = 'succeeded';
                state.starredBoards = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchStarredBoards.rejected, (state, action) => {
                state.starredStatus = 'failed';
                state.error = action.payload;
            })
            // Fetch recent view boards
            .addCase(fetchRecentBoards.pending, (state) => {
                state.recentStatus = 'loading';
            })
            .addCase(fetchRecentBoards.fulfilled, (state, action) => {
                state.recentStatus = 'succeeded';
                state.recentBoards = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchRecentBoards.rejected, (state, action) => {
                state.recentStatus = 'failed';
                state.error = action.payload;
            });
    },
});

export const {
    clearBoardsCache,
    optimisticUpdateBoardInLists,
    optimisticAddBoardToLists,
    optimisticRemoveBoardFromLists,
    optimisticRemoveFromArchivedBoards
} = boardsSlice.actions;

export default boardsSlice.reducer;